import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTRPC } from '../../lib/trpc';
import { StatusBadge } from '../ui/Badge';

/**
 * Every unit at this address, not just the one this job is about.
 *
 * A tech standing on a commercial roof needs to know there are four rooftop units and
 * which one they're at — the job tags one, the building has the rest, and "the one on the
 * north side" is not a serial number. On a house it's usually one furnace and this card
 * stays out of the way.
 *
 * Read-only on purpose: tagging happens on the job, where it belongs to a decision about
 * what this work touched.
 */
export function UnitsAtAddressCard({
  propertyId,
  linkTo = 'office',
}: {
  propertyId: string;
  /** The tech shell has no equipment route, so its rows are plain text. */
  linkTo?: 'office' | 'none';
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.equipment.forProperty.queryOptions({ propertyId }));

  // Quiet unless there is something worth saying — one unit is what the job already shows.
  if (q.isPending || q.isError || !q.data || q.data.length < 2) return null;

  return (
    <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {q.data.length} units at this address
      </p>
      <ul className="space-y-1.5 text-sm">
        {q.data.map((e) => {
          const detail =
            [e.make, e.model].filter(Boolean).join(' ') +
            (e.serial ? ` · #${e.serial}` : '');
          const body = (
            <>
              <span className="min-w-0">
                <span className="block truncate font-medium">{e.name}</span>
                {detail.trim() ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {detail}
                  </span>
                ) : null}
              </span>
              {!e.active ? <StatusBadge tone="neutral">retired</StatusBadge> : null}
            </>
          );
          return (
            <li key={e.id} className="flex items-center justify-between gap-2">
              {linkTo === 'office' ? (
                <Link
                  to="/equipment/$equipmentId"
                  params={{ equipmentId: e.id }}
                  className="flex min-w-0 flex-1 items-center justify-between gap-2 hover:underline"
                >
                  {body}
                </Link>
              ) : (
                <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                  {body}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
